import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Fingerprint, KeyRound, Languages, LogOut, Moon, PanelLeft, Search, Sun, User } from 'lucide-react';
import { adminPasskeyController } from '@/api/generated/endpoints';
import { useAuth } from '@/auth/AuthProvider';
import { passkeysSupported } from '@/features/auth/passkey';
import { useResult } from '@/lib/query';
import { LiveRidership } from '@/components/LiveRidership';
import { MarketSelect } from '@/components/MarketSelect';
import { Oppenheimer } from '@/components/Oppenheimer';
import { VeoMark } from '@/components/VeoMark';
import { LANGUAGES, setLanguage, type Language } from '@/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  Kbd,
  cn,
  focusRing,
  setColorMode,
  useColorMode,
  useBelow,
  useHotkey,
} from '@/ui';
import { CommandPalette } from './CommandPalette';
import type { ShellContext } from './shellContext';
import { NavDrawer, Sidebar } from './Sidebar';
import { WorkspacePages, WorkspaceProvider, WorkspaceTabs } from './Workspace';

const SIDEBAR_KEY = 'manhattan.sidebar';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

function initials(name: string): string {
  return (
    name
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join('') || '?'
  );
}

/** Light/dark switch (light is the default; the choice is remembered per browser). */
function ColorModeToggle() {
  const { t } = useTranslation('shell');
  const mode = useColorMode();
  const next = mode === 'dark' ? 'light' : 'dark';
  return <IconButton label={next === 'dark' ? t('header.toDark') : t('header.toLight')} icon={mode === 'dark' ? <Sun /> : <Moon />} onClick={() => setColorMode(next)} />;
}

function AccountMenu() {
  const { t, i18n } = useTranslation('shell');
  const { me, signOut } = useAuth();
  const mode = useColorMode();
  const navigate = useNavigate();
  const name = me?.name ?? me?.email ?? '';
  // a quiet nudge (a dot and a menu item) while the admin has no passkey; shares the Profile's query
  const supported = passkeysSupported();
  const passkeys = useResult(['auth', 'passkeys'], () => adminPasskeyController.list(), supported && !!me);
  const needsPasskey = supported && passkeys.isSuccess && (passkeys.data ?? []).length === 0;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('header.account')}
        className={cn('m-0 flex h-9 cursor-pointer items-center gap-2 rounded-md border-0 bg-transparent px-1.5 font-sans text-sm text-ink hover:bg-muted', focusRing)}
      >
        <span className="relative flex size-7 items-center justify-center rounded-full bg-navy text-xs font-bold text-canvas" aria-hidden>
          {initials(name)}
          {needsPasskey && <span data-testid="passkey-dot" className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-canvas bg-primary" />}
        </span>
        <span className="hidden max-w-40 truncate font-bold xl:inline">{name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {me?.email && <DropdownMenuLabel>{me.email}</DropdownMenuLabel>}
        {needsPasskey && (
          <DropdownMenuItem icon={<Fingerprint />} onSelect={() => navigate('/profile#passkeys')}>
            {t('header.setUpPasskey')}
            <span className="ml-auto pl-3 text-xs text-ink-muted">{t('header.setUpPasskeyHint')}</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem icon={<User />} onSelect={() => navigate('/profile')}>
          {t('header.profile')}
        </DropdownMenuItem>
        <DropdownMenuItem icon={<KeyRound />} onSelect={() => navigate('/profile')}>
          {t('tempPassword.action')}
          {me?.passwordUpdated === false && (
            <span className="ml-auto pl-3 text-xs text-ink-muted">{t('tempPassword.label')}</span>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center gap-1.5">
          <Languages size={14} aria-hidden />
          {t('header.language')}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup value={i18n.resolvedLanguage} onValueChange={(lng) => void setLanguage(lng as Language)}>
          {LANGUAGES.map((l) => (
            <DropdownMenuRadioItem key={l.code} value={l.code} lang={l.code}>
              {l.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        {/* phones have no room for the header toggle */}
        <DropdownMenuItem className="sm:hidden" icon={mode === 'dark' ? <Sun /> : <Moon />} onSelect={() => setColorMode(mode === 'dark' ? 'light' : 'dark')}>
          {mode === 'dark' ? t('header.toLight') : t('header.toDark')}
        </DropdownMenuItem>
        <DropdownMenuItem icon={<LogOut />} onSelect={signOut}>
          {t('header.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The signed-in console: a white header (brand, working market, global search, colour mode,
 * account), the section sidebar, and the routed page. Pages own their PageHeader; the content area
 * keeps room at the bottom for the assistant button.
 */
export function AppShell() {
  const { t } = useTranslation('shell');
  // phones: a drawer; tablets: the icon rail (the full sidebar would squeeze a table); desktop: the admin's choice
  const phone = useBelow('md');
  const narrow = useBelow('lg');
  const [preferCollapsed, setPreferCollapsed] = useState(readCollapsed);
  const [searching, setSearching] = useState(false);
  const collapsed = narrow || preferCollapsed;
  const shell = useMemo<ShellContext>(() => ({ openSearch: () => setSearching(true) }), []);

  const toggleSidebar = () => {
    const next = !preferCollapsed;
    setPreferCollapsed(next);
    try {
      localStorage.setItem(SIDEBAR_KEY, next ? 'collapsed' : 'expanded');
    } catch {
      /* the choice lasts for this page only */
    }
  };

  useHotkey('/', () => setSearching(true), { description: t('search.shortcut'), group: 'general' });
  useHotkey('mod+k', () => setSearching(true), { description: t('search.shortcutAnywhere'), group: 'general', allowInInputs: true });

  // Mission (ride goal) paused for now: put <RideGoalProvider> inside the WorkspaceProvider and
  // <GoalBar /> under the header to bring it back (components/goal); without the provider no goal
  // is fetched or shown.
  return (
    <WorkspaceProvider outletContext={shell}>
        <div className="flex h-dvh flex-col bg-canvas font-sans text-base text-ink">
          <header className="flex h-14 shrink-0 items-center gap-1.5 border-b border-line bg-surface px-2 sm:gap-3 sm:px-3">
            {phone && <NavDrawer />}
            {!narrow && <IconButton label={collapsed ? t('header.expandSidebar') : t('header.collapseSidebar')} icon={<PanelLeft />} onClick={toggleSidebar} />}
            <Link to="/" className={cn('flex shrink-0 items-center gap-2 rounded-md text-heading no-underline', focusRing)}>
              <VeoMark size={26} />
              <span className="hidden font-display text-lg font-bold sm:inline">Manhattan</span>
            </Link>
            <MarketSelect className="w-32 shrink-0 sm:ml-1 sm:w-52" />
            <LiveRidership />
            <button
              type="button"
              onClick={() => setSearching(true)}
              aria-label={t('header.search')}
              aria-keyshortcuts="/ Meta+K Control+K"
              className={cn(
                // a magnifier on phones, the full search box from md
                'm-0 flex h-9 w-9 shrink-0 cursor-text items-center justify-center gap-2.5 rounded-lg border border-line-strong bg-subtle px-0 text-left font-sans text-base text-ink-muted hover:border-ink-faint md:mx-auto md:w-auto md:min-w-0 md:max-w-md md:flex-1 md:shrink md:justify-start md:px-3',
                focusRing,
              )}
            >
              <Search size={16} aria-hidden className="shrink-0" />
              <span className="hidden truncate md:inline">{t('header.search')}</span>
              <Kbd keys="/" className="ml-auto hidden md:inline-flex" />
            </button>
            <div className="ml-auto flex shrink-0 items-center gap-1 md:ml-0">
              <span className="hidden sm:contents">
                <ColorModeToggle />
              </span>
              <AccountMenu />
            </div>
          </header>

          <div className="flex min-h-0 flex-1">
            {!phone && <Sidebar collapsed={collapsed} />}
            {/* relative: absolutely placed bits (screen-reader text in sideways-scrolling tables) stay inside, not widening a phone's page */}
            <main className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
              <WorkspaceTabs />
              <WorkspacePages />
            </main>
          </div>

          <CommandPalette open={searching} onOpenChange={setSearching} />
          <Oppenheimer />
        </div>
    </WorkspaceProvider>
  );
}
